import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Upload, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    heroImage: '',
    heroImagePosition: 'center',
    heroImageZoom: 1.0
  });

  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setSettings(response.data);
      setImagePreview(response.data.heroImage);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setSettings({ ...settings, heroImage: url });
    setImagePreview(url);
  };

  const handlePositionChange = (position) => {
    setSettings({ ...settings, heroImagePosition: position });
  };

  const handleZoomChange = (e) => {
    const zoom = parseFloat(e.target.value);
    setSettings({ ...settings, heroImageZoom: zoom });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        settings,
        { withCredentials: true }
      );
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-gray-600"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Admin
            </Button>
            <h1 className="text-4xl font-bold text-gray-900">Website Settings</h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-rose-500 hover:bg-rose-600 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Hero Section Settings</h2>

            {/* Image URL */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hero Image URL
              </label>
              <input
                type="text"
                value={settings.heroImage}
                onChange={handleImageUrlChange}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                Paste the URL of your hero image here
              </p>
            </div>

            {/* Position Controls */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Image Position
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['top', 'center', 'bottom'].map(position => (
                  <button
                    key={position}
                    onClick={() => handlePositionChange(position)}
                    className={`px-4 py-3 rounded-lg font-medium capitalize transition-all duration-200 ${
                      settings.heroImagePosition === position
                        ? 'bg-rose-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {position}
                  </button>
                ))}
              </div>
            </div>

            {/* Zoom Control */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Image Zoom: {settings.heroImageZoom.toFixed(1)}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={settings.heroImageZoom}
                onChange={handleZoomChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0.5x (Zoom Out)</span>
                <span>2.0x (Zoom In)</span>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Instructions:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Upload your image to a service or use existing URL</li>
                <li>• Adjust position (Top/Center/Bottom) to frame properly</li>
                <li>• Use zoom slider to fit the image perfectly</li>
                <li>• Preview changes on the right before saving</li>
              </ul>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Live Preview</h2>
            
            <div className="relative h-[400px] rounded-lg overflow-hidden border-2 border-gray-200">
              {imagePreview ? (
                <>
                  <img
                    src={imagePreview}
                    alt="Hero Preview"
                    className="w-full h-full"
                    style={{
                      objectFit: 'cover',
                      objectPosition: settings.heroImagePosition,
                      transform: `scale(${settings.heroImageZoom})`
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center justify-center">
                    <div className="text-center text-white">
                      <h1 className="text-4xl font-bold mb-4">Pure. Natural. Authentic Dairy Products</h1>
                      <p className="text-xl">Experience the richness of traditional dairy products</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Upload className="h-12 w-12 mx-auto mb-2" />
                    <p>Enter image URL to see preview</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2 text-sm text-gray-600">
              <p><strong>Current Position:</strong> {settings.heroImagePosition}</p>
              <p><strong>Current Zoom:</strong> {settings.heroImageZoom}x</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
